import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { DbConfigResponse, ApiResponse } from '../models/parameter.model';
import { AuctionVehicle, LoadAuctionVehicle } from '../models/vehicle.model';
import { BidRequest } from '../models/bid.model';
import { WatchItem } from '../models/watchlist.model';
import {
  AccessUserRequest,
  AccessUserResponse,
  User,
  UserDealer,
  UserDealerRequest,
} from '../models/user.model';
import { VehicleCheckDetaisRequest, VehicleCheckDetaisResponse, } from '../models/vehicleCheckDetais';

@Injectable({
  providedIn: 'root',
})
export class DealerBiddingService {
  private http = inject(HttpClient);
  private getHeaders(): HttpHeaders {
    const token = localStorage.getItem('access_token') || '';
    const host = window.location.hostname.toLowerCase();
    const domainExt = host.match(/\.(com|net)/)?.[0] || '';
    const domainStr = domainExt === '.com' ? 'com' : 'net';
    return new HttpHeaders()
      .set('Authorization', `Bearer ${token}`)
      .set('domain', domainStr)
      .set('Content-Type', 'application/json')
      .set('Accept', 'application/json');
  }
  private normalizeResponse<T>(response: ApiResponse<T> | T): ApiResponse<T> {
    if (
      response &&
      typeof response === 'object' &&
      'success' in response &&
      'data' in response
    ) {
      return response as ApiResponse<T>;
    }

    return { success: true, data: response as T, error: null };
  }

  private toErrorResponse<T>(error: any): ApiResponse<T> {
    const message =
      error?.error?.message ||
      error?.message ||
      'Request failed. Please try again later.';
    const code = error?.error?.code || `${error?.status ?? 'HTTP_ERROR'}`;
    return { success: false, data: null, error: { code, message } };
  }

  getDbConfig(): Observable<ApiResponse<DbConfigResponse>> {
    return this.http
      .get<ApiResponse<DbConfigResponse> | DbConfigResponse>(
        '/db',
        { headers: this.getHeaders() }
      )
      .pipe(
        map((response) => this.normalizeResponse<DbConfigResponse>(response)),
        catchError((error) => of(this.toErrorResponse<DbConfigResponse>(error)))
      );
  }

  getActiveVehicles(): Observable<ApiResponse<AuctionVehicle[]>> {
    return this.http
      .get<ApiResponse<AuctionVehicle[]> | AuctionVehicle[]>(
        '/db/vehicles/active',
        { headers: this.getHeaders() }
      )
      .pipe(
        map((response) => this.normalizeResponse<AuctionVehicle[]>(response)),
        catchError((error) =>
          of(this.toErrorResponse<AuctionVehicle[]>(error))
        )
      );
  }

  getActiveVehiclesCheckDetails(
    request: VehicleCheckDetaisRequest
  ): Observable<ApiResponse<VehicleCheckDetaisResponse>> {
    return this.http
      .post<ApiResponse<VehicleCheckDetaisResponse> | VehicleCheckDetaisResponse>(
        '/db/vehiclesCheckDetails',
        request,
        { headers: this.getHeaders() }
      )
      .pipe(
        map((response) =>
          this.normalizeResponse<VehicleCheckDetaisResponse>(response)
        ),
        catchError((error) =>
          of(this.toErrorResponse<VehicleCheckDetaisResponse>(error))
        )
      );
  }

  placeBid(bid: BidRequest): Observable<ApiResponse<null>> {
    return this.http
      .post<ApiResponse<null> | null>('/db/bids', bid, {
        headers: this.getHeaders(),
      })
      .pipe(
        map((response) => this.normalizeResponse<null>(response)),
        catchError((error) => of(this.toErrorResponse<null>(error)))
      );
  }

  matchOffer(vin: string): Observable<ApiResponse<null>> {
    return this.http
      .post<ApiResponse<null> | null>(
        '/db/match-offer',
        { vin: vin },
        { headers: this.getHeaders() }
      )
      .pipe(
        map((response) => this.normalizeResponse<null>(response)),
        catchError((error) => of(this.toErrorResponse<null>(error)))
      );
  }

  cancelDeal(vin: string): Observable<ApiResponse<null>> {
    return this.http
      .post<ApiResponse<null> | null>(
        '/db/deals/cancel',
        { vin: vin },
        { headers: this.getHeaders() }
      )
      .pipe(
        map((response) => this.normalizeResponse<null>(response)),
        catchError((error) => of(this.toErrorResponse<null>(error)))
      );
  }

  getSellingVehicles(agentCode: string): Observable<ApiResponse<AuctionVehicle[]>> {
    return this.http
      .post<ApiResponse<AuctionVehicle[]> | AuctionVehicle[]>(
        '/db/vehicles/selling',
        { agentCode: agentCode },
        { headers: this.getHeaders() }
      )
      .pipe(
        map((response) => this.normalizeResponse<AuctionVehicle[]>(response)),
        catchError((error) =>
          of(this.toErrorResponse<AuctionVehicle[]>(error))
        )
      );
  }
  getPurchasingVehicles(
    agentCode: string
  ): Observable<ApiResponse<AuctionVehicle[]>> {
    return this.http
      .post<ApiResponse<AuctionVehicle[]> | AuctionVehicle[]>(
        '/db/vehicles/purchasing',
        { agentCode: agentCode },
        { headers: this.getHeaders() }
      )
      .pipe(
        map((response) => this.normalizeResponse<AuctionVehicle[]>(response)),
        catchError((error) =>
          of(this.toErrorResponse<AuctionVehicle[]>(error))
        )
      );
  }

  addVehicleWatch(watch: WatchItem): Observable<ApiResponse<null>> {
    return this.http
      .post<ApiResponse<null> | null>('/watchlist/add', watch, {
        headers: this.getHeaders(),
      })
      .pipe(
        map((response) => this.normalizeResponse<null>(response)),
        catchError((error) => of(this.toErrorResponse<null>(error)))
      );
  }

  removeVehicleWatch(watchListRequest: {
    agent: string;
    agentCode: string;
    adgCode: string;
  }): Observable<ApiResponse<null>> {
    return this.http
      .post<ApiResponse<null> | null>(
        '/watchlist/delete',
        watchListRequest,
        { headers: this.getHeaders() }
      )
      .pipe(
        map((response) => this.normalizeResponse<null>(response)),
        catchError((error) => of(this.toErrorResponse<null>(error)))
      );
  }

  getWatchlist(watchListRequest: {
    agent: string;
    agentCode: string;
  }): Observable<ApiResponse<WatchItem[]>> {
    return this.http
      .post<ApiResponse<WatchItem[]> | WatchItem[]>(
        '/watchlist/items',
        watchListRequest,
        { headers: this.getHeaders() }
      )
      .pipe(
        map((response) => this.normalizeResponse<WatchItem[]>(response)),
        catchError((error) => of(this.toErrorResponse<WatchItem[]>(error)))
      );
  }

  getDealerUsers(request: {
    dealerCode: string;
    accessGranted: boolean;
  }): Observable<ApiResponse<User[]>> {
    return this.http
      .post<ApiResponse<User[]> | User[]>(
        '/users/dealer-users',
        request,
        { headers: this.getHeaders() }
      )
      .pipe(
        map((response) => this.normalizeResponse<User[]>(response)),
        catchError((error) => of(this.toErrorResponse<User[]>(error)))
      );
  }

  confirmUserExists(request: { cNumber: string }): Observable<ApiResponse<boolean>> {
    return this.http
      .post<ApiResponse<boolean> | boolean>(
        '/users/confirm-user',
        request,
        { headers: this.getHeaders() }
      )
      .pipe(
        map((response) => this.normalizeResponse<boolean>(response)),
        catchError((error) => of(this.toErrorResponse<boolean>(error)))
      );
  }
  confirmUserAccess(request: { cNumber: string }): Observable<ApiResponse<boolean>> {
    return this.http
      .post<ApiResponse<boolean> | boolean>(
        '/users/confirm-access',
        request,
        { headers: this.getHeaders() }
      )
      .pipe(
        map((response) => this.normalizeResponse<boolean>(response)),
        catchError((error) => of(this.toErrorResponse<boolean>(error)))
      );
  }

  confirmUserDealerAccess(request: {
    cNumber: string;
    dealerCode: string;
  }): Observable<ApiResponse<AccessUserResponse>> {
    return this.http
      .post<ApiResponse<AccessUserResponse> | AccessUserResponse>(
        '/users/confirm-user-access',
        request,
        { headers: this.getHeaders() }
      )
      .pipe(
        map((response) =>
          this.normalizeResponse<AccessUserResponse>(response)
        ),
        catchError((error) =>
          of(this.toErrorResponse<AccessUserResponse>(error))
        )
      );
  }
  grantUserAccess(request: {
    cNumber: string;
    dealerCode: string;
    accessGranted: boolean;
  }): Observable<ApiResponse<null>> {
    return this.http
      .post<ApiResponse<null> | null>(
        '/users/grant-access',
        request,
        { headers: this.getHeaders() }
      )
      .pipe(
        map((response) => this.normalizeResponse<null>(response)),
        catchError((error) => of(this.toErrorResponse<null>(error)))
      );
  }

  deleteUser(request: {
    cNumber: string;
    dealerCode: string;
  }): Observable<ApiResponse<null>> {
    return this.http
      .post<ApiResponse<null> | null>('/users/delete-user', request, {
        headers: this.getHeaders(),
      })
      .pipe(
        map((response) => this.normalizeResponse<null>(response)),
        catchError((error) => of(this.toErrorResponse<null>(error)))
      );
  }
  requestAccess(user: AccessUserRequest): Observable<ApiResponse<boolean>> {
    return this.http
      .post<ApiResponse<boolean> | boolean>(
        '/users/request-access',
        user,
        { headers: this.getHeaders() }
      )
      .pipe(
        map((response) => this.normalizeResponse<boolean>(response)),
        catchError((error) => of(this.toErrorResponse<boolean>(error)))
      );
  }
  addVehicle(vehicle: LoadAuctionVehicle): Observable<ApiResponse<null>> {
    return this.http
      .post<ApiResponse<null> | null>('/db/vehicles', vehicle, {
        headers: this.getHeaders(),
      })
      .pipe(
        map((response) => this.normalizeResponse<null>(response)),
        catchError((error) => of(this.toErrorResponse<null>(error)))
      );
  }

  acceptOffer(vin: string): Observable<ApiResponse<null>> {
    return this.http
      .post<ApiResponse<null> | null>(
        '/db/deals/accept',
        { vin: vin },
        { headers: this.getHeaders() }
      )
      .pipe(
        map((response) => this.normalizeResponse<null>(response)),
        catchError((error) => of(this.toErrorResponse<null>(error)))
      );
  }
  getDealerUser(userDealerRequest: UserDealerRequest): Observable<ApiResponse<UserDealer>> {
    return this.http
      .post<ApiResponse<UserDealer> | UserDealer>(
        '/cross-border/user-dealer',
        userDealerRequest,
        { headers: this.getHeaders() }
      )
      .pipe(
        map((response) => this.normalizeResponse<UserDealer>(response)),
        catchError((error) => of(this.toErrorResponse<UserDealer>(error)))
      );
  }

  addMultipleVehicle(
    file: File,
    user: string,
    dealerName: string,
    dealerCode: string
  ): Observable<ApiResponse<unknown>> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('user', user);
    formData.append('dealerName', dealerName);
    formData.append('dealerCode', dealerCode);
    return this.http
      .post<ApiResponse<unknown> | unknown>(
        '/db/multipleVehicles',
        formData
      )
      .pipe(
        map((response) => this.normalizeResponse<unknown>(response)),
        catchError((error) => of(this.toErrorResponse<unknown>(error)))
      );
  }

  /**
   * Fetch condition report by VIN
   * @param vin Vehicle VIN
   * @returns Observable of Blob containing the condition report
   */
  getConditionReport(vin: string): Observable<Blob> {
    return this.http.post(
      '/vehicles/condition-report',
      { vin: vin },
      {
        headers: this.getHeaders(),
        responseType: 'blob',
      }
    );
  }

  /**
   * Fetch media by S3 key
   * @param key S3 key for the media file
   * @returns Observable of Blob containing the media file
   */
  getMediaByKey(key: string): Observable<Blob> {
    return this.http.post(
      '/media/by-key',
      { key: key },
      {
        headers: this.getHeaders(),
        responseType: 'blob',
      }
    );
  }
  getDealerNameVyCode(dealerCode: string): Observable<ApiResponse<string>> {
    return this.http
      .post<
        ApiResponse<string> | string
      >('/db/dealerNameByCode', { dealerCode: dealerCode }, { headers: this.getHeaders() })
      .pipe(
        map((response) => this.normalizeResponse<string>(response)),
        catchError((error) => of(this.toErrorResponse<string>(error)))
      );
  }
  concludeDeal(vin: string): Observable<ApiResponse<null>> {
    return this.http
      .post<ApiResponse<null> | null>(
        '/db/deals/conclude',
        { vin: vin },
        { headers: this.getHeaders() }
      )
      .pipe(
        map((response) => this.normalizeResponse<null>(response)),
        catchError((error) => of(this.toErrorResponse<null>(error)))
      );
  }
}
